/**
 * Overlay2D — 2D打重ね image overlay (ticket #8424, used by 事務所画面 #8428 and 現場画面 #8431).
 *
 * Replaces the 3D model area when the system is in 2D display mode. It shows two PNG "layer"
 * images side by side (left = current layer N 先行, right = next layer N+1 後続 — per the
 * 画面設計書 mockups 事務所_詳細②-1 / 現場_詳細②_4) and draws a status-coloured marker over
 * each mesh cell. Cells + their pixel positions are derived by re-scanning the PNG with
 * utils/pngMeshScan (same scan used at registration in #8426), then coloured by the mesh
 * status passed in `meshList`. Layer navigation is the spec's 「n／m層目」 labels plus
 * ＜＜ ＜ ＞ ＞＞ pair-base buttons (事務所②-1 ⑫〜⑰, 現場②_4 ⑳〜㉕).
 *
 * ⚠️ NEEDS-VALIDATION against a real customer PNG: the marker detection + coordinate mapping
 * live in utils/pngMeshScan.ts (tunable thresholds). Colours below follow the ticket's colour
 * legend but exact hex values should be confirmed with 秋本's spec.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@services'
import { parseLayerFromFilename, isMarkerPixel, loadImageData, markerRowsOf } from '@utils/pngMeshScan'
import { STATUS_MESH, WARNING_STATUS } from '../const/constants'

// Mesh status/row info supplied by the parent screen (from Location/GetWithMesh).
export interface MeshCellStatus {
    id?: string
    layer: number
    row: number
    status?: number         // index into STATUS_MESH: 0=未打設,1=打設中,2=打設済
    // Raw BE warning code string: WARNING_STATUS[0]='WarningTimeExceeded' (警告),
    // WARNING_STATUS[1]='TimeLimitExceeded' (制限). '' / undefined = none.
    warningStatus?: string
    pumpId?: string | null
    pumpMismatch?: boolean   // true = the cell belongs to a pump other than the selected one (gray while 未打設)
    // Gray another pump's cell whatever its status, as 3D does; only the 警告 / 制限 colours
    // still show. Off = gray 未打設 cells only (the original 事務所②-1 ⑤ rule). 現場画面
    // (SKY-172) and 事務所画面 (SKY-173) both turn it on at the customer's request.
    grayAnyStatus?: boolean
}

interface Attachment { fileName: string; fileUrl: string; layer: number }
// rows = the mesh rows (Blue values) present in the PNG — see markerRowsOf (SKY-193)
interface LayerImage { url: string; rows: number[]; naturalW: number; naturalH: number; imageData?: ImageData }

interface Overlay2DProps {
    locationId: string
    meshList?: MeshCellStatus[]
    /** Optional controlled active layer; if omitted the component manages it internally. */
    activeLayer?: number
    onLayerChange?: (layer: number) => void
    /**
     * Fired when a cell marker is tapped. 現場 opens the 打設開始/終了/取消 modal;
     * 事務所 uses it for the orange cell selection (事務所②-1 ⑤).
     * SKY-165: the 「下の層が未打設」 guard is decided by the caller from the mesh's
     * underMeshIds (the BE's UnderMesh relations), not from pixel positions here.
     */
    onCellClick?: (cell: { layer: number; row: number; meshId?: string }) => void
    /**
     * Cells drawn in orange as the current selection (事務所②-1 ⑤). SKY-131: a list — tapping a
     * 運搬情報一覧 row selects every mesh that truck poured, possibly on several layers.
     */
    selectedCells?: { layer: number; row: number }[]
    /**
     * SKY-124: whether the 打重ね view is zoomed (拡大). When zoomed the mesh fills the panel
     * (object-fit); otherwise it keeps the original capped size. Defaults to not-zoomed.
     */
    fullScreen?: boolean
}

// Colour legend. SKY-134: the 2D mesh must match the 3D model / list colours exactly, so
// 打設中/打設済 use the SAME hex as the list rows (App.css .bg-during-casting / .bg-end-casting,
// see ListMesh.tsx): 打設中=#fde699 (bg-during-casting), 打設済=#c6e0b4 (bg-end-casting).
// (The ticket text swapped the two hex and mislabelled #fde699 as 緑; the goal「3Dモデルと相違」
//  is met by matching the app's verified 3D convention.)
// SKY-32: the mesh is rendered by repainting the PNG's own pixels (recolorMeshToDataUrl),
// so the status colours are defined directly in RGB below.
const COLOR_RGB: Record<string, [number, number, number]> = {
    before: [255, 255, 255],        // 未打設 white
    during: [253, 230, 153],        // 打設中 #fde699 (= .bg-during-casting)
    finish: [198, 224, 180],        // 打設済 #c6e0b4 (= .bg-end-casting)
    grayPump: [158, 158, 158],      // 別ポンプ選択中 gray
    overLimit: [248, 79, 81],       // 制限時間超過 #f84f51 (= .bg-time-limit-exceeded)
    overWarning: [247, 102, 255],   // 警告時間超過 #f766ff (= .bg-warning-time-exceeded)
    selected: [255, 152, 0],        // 選択中 orange
}
function cellColorRgb(m?: MeshCellStatus): [number, number, number] {
    if (!m) return COLOR_RGB.before
    if (m.warningStatus === WARNING_STATUS[1]) return COLOR_RGB.overLimit
    if (m.warningStatus === WARNING_STATUS[0]) return COLOR_RGB.overWarning
    if (m.pumpMismatch && m.grayAnyStatus) return COLOR_RGB.grayPump
    if (m.status === STATUS_MESH.indexOf('DuringPumping')) return COLOR_RGB.during
    if (m.status === STATUS_MESH.indexOf('FinishPumping')) return COLOR_RGB.finish
    if (m.pumpMismatch) return COLOR_RGB.grayPump
    return COLOR_RGB.before
}

/**
 * SKY-32 / SKY-124: repaint the customer PNG's OWN pixels by mesh status instead of
 * scanning it and drawing axis-aligned rectangles. Drawing rectangles over a
 * perspective (trapezoidal) mesh produced a staggered / mis-aligned grid
 * (「メッシュごとに少しずれて表示」). Recolouring the source pixels keeps every cell's
 * exact drawn shape, perspective and alignment; only the fill colour changes.
 *   - marker pixel (red-encoded cell) -> its status colour (Blue channel = row).
 *   - black grid line / direction arrow -> kept.
 *   - white background / transparent -> transparent (the gray 打重ね panel shows through).
 */
function recolorMeshToDataUrl(
    src: ImageData,
    colorForRow: (blue: number) => [number, number, number],
): string {
    const { width, height, data } = src
    const canvas = document.createElement('canvas')
    canvas.width = width; canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    const out = ctx.createImageData(width, height)
    const o = out.data
    const total = width * height
    for (let p = 0; p < total; p++) {
        const i = p * 4
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3]
        if (isMarkerPixel(r, g, b, a)) {
            const c = colorForRow(b)
            o[i] = c[0]; o[i + 1] = c[1]; o[i + 2] = c[2]; o[i + 3] = 255
        } else if (a >= 128 && r < 110 && g < 110 && b < 110) {
            o[i] = r; o[i + 1] = g; o[i + 2] = b; o[i + 3] = 255 // black grid line / arrow
        } else {
            o[i + 3] = 0 // white background / transparent -> let the gray panel show
        }
    }
    ctx.putImageData(out, 0, 0)
    return canvas.toDataURL()
}

const IMG_BASE = process.env.REACT_APP_API_ENDPOINT || '/'
const imgSrc = (fileUrl: string) => `${IMG_BASE}${fileUrl}`.replace(/([^:])\/\//g, '$1/')

const Overlay2D: React.FC<Overlay2DProps> = ({
    locationId, meshList = [], activeLayer, onLayerChange, onCellClick, selectedCells, fullScreen = false,
}) => {
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [images, setImages] = useState<Record<number, LayerImage>>({})
    const [internalLayer, setInternalLayer] = useState<number>(1)
    const [loading, setLoading] = useState(false)
    // SKY-32: per-layer recoloured mesh (data URL), rebuilt when the status changes.
    const [recolored, setRecolored] = useState<Record<number, string>>({})
    // SKY-193: layers being downloaded/decoded right now, so a quick ＞＞ or a re-render while a
    // load is in flight does not fetch and decode the same PNG twice; the images cache is read
    // through a ref for the same reason (the effect closure would hold a stale copy).
    const loadingRef = useRef<Set<number>>(new Set())
    const imagesRef = useRef(images); imagesRef.current = images
    // SKY-193: bumped on every location change so a decode still in flight for the previous
    // location cannot land in the new location's cache.
    const epochRef = useRef(0)
    // SKY-193: last recoloured image per layer with the inputs it was built from — see effect 3.
    const recolorCache = useRef<Record<number, { src: ImageData; sig: string; url: string }>>({})

    const layers = useMemo(
        () => Array.from(new Set(attachments.map(a => a.layer))).sort((a, b) => a - b),
        [attachments],
    )
    // A 打重ね view is an adjacent PAIR (先行 N, 後続 N+1). Only a layer that HAS a partner
    // above it (N+1 exists) can be a base — otherwise the 後続 panel would be empty. This is
    // why the top layer (e.g. layer 5 of 5) must not be selectable on its own (#8505/#8506).
    const baseLayers = useMemo(() => {
        const withPartner = layers.filter(l => layers.includes(l + 1))
        return withPartner.length ? withPartner : layers
    }, [layers])
    const requestedLayer = activeLayer ?? internalLayer
    // clamp the active base into a valid pair base (fallback to the highest valid base)
    const layerN = baseLayers.includes(requestedLayer)
        ? requestedLayer
        : (baseLayers.length ? baseLayers[baseLayers.length - 1] : requestedLayer)
    const setLayer = (l: number) => { onLayerChange ? onLayerChange(l) : setInternalLayer(l) }

    // status lookup by `${layer}-${row}`
    const statusMap = useMemo(() => {
        const m: Record<string, MeshCellStatus> = {}
        meshList.forEach(x => { m[`${x.layer}-${x.row}`] = x })
        return m
    }, [meshList])

    // SKY-131: STABLE string signatures of the status data + selection. The parent rebuilds
    // `meshList` and `selectedCells` as new object references on every render, so keying the
    // recolour effect on those objects re-ran it every render → an infinite recolour loop that
    // left data-heavy 事務所 locations stuck on 「読み込み中」 (mesh never became tappable, so the
    // 運搬情報一覧 never turned red). Equal strings are Object.is-equal, so the effect now only
    // re-runs when the actual statuses/selection change.
    const statusSig = useMemo(
        () => meshList.map(x => `${x.layer}-${x.row}:${x.status ?? ''}:${x.warningStatus ?? ''}:${x.pumpMismatch ? 1 : 0}${x.grayAnyStatus ? 1 : 0}`).sort().join('|'),
        [meshList],
    )
    // sorted + de-duplicated so the same selection always gives the same string
    const selSig = Array.from(new Set((selectedCells || []).map(c => `${c.layer}-${c.row}`))).sort().join('|')

    // 1) fetch PNG attachments for the location
    useEffect(() => {
        // reset the scan cache — it is keyed by layer number, so a location change would
        // otherwise keep the previous location's (possibly empty) scan for layers 1,2,…
        setImages({})
        // SKY-193: the recolour cache and the shown images are keyed by layer NUMBER too — drop
        // them, otherwise the previous location's layer 1/2 stays on screen (or is served from the
        // cache) until the new PNGs are decoded.
        epochRef.current += 1
        loadingRef.current.clear()
        recolorCache.current = {}
        setRecolored({})
        if (!locationId) { setAttachments([]); return }
        let alive = true
        setLoading(true)
        api.locationAPI.getAttachments(locationId)
            .then((res: any) => {
                if (!alive) return
                const list: Attachment[] = (res?.data ?? []).map((a: any) => ({
                    fileName: a.fileName,
                    fileUrl: a.fileUrl,
                    layer: parseLayerFromFilename(a.fileName),
                }))
                setAttachments(list)
                const ls = Array.from(new Set(list.map(a => a.layer))).sort((a, b) => a - b)
                if (ls.length && activeLayer == null) setInternalLayer(ls[0])
            })
            .catch(() => { if (alive) setAttachments([]) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
        // Intentionally only re-run on locationId change (attachments/activeLayer read once).
    }, [locationId])

    // 2) decode the PNGs for layer N and N+1 (only when not cached), then prefetch their neighbours
    useEffect(() => {
        const epoch = epochRef.current
        const has = (l: number) => attachments.some(a => a.layer === l)
        const load = async (l: number) => {
            if (imagesRef.current[l] || loadingRef.current.has(l)) return
            const att = attachments.find(a => a.layer === l)
            if (!att) return
            loadingRef.current.add(l)
            const url = imgSrc(att.fileUrl)
            try {
                // SKY-32: keep the source pixels so we can repaint the mesh by status
                // (faithful perspective) instead of drawing rectangles over it.
                // SKY-193: decode ONCE. This used to run scanPngToCells (a decode plus a flood
                // fill) and then loadImageData (a second decode of the same file); the scan's
                // only use here was the list of rows present in the PNG, which one pass over
                // the decoded pixels gives (markerRowsOf).
                const imageData = await loadImageData(url)
                const rows = markerRowsOf(imageData)
                if (epochRef.current !== epoch) return
                setImages(prev => ({ ...prev, [l]: { url, rows, naturalW: imageData.width, naturalH: imageData.height, imageData } }))
            } catch {
                if (epochRef.current !== epoch) return
                setImages(prev => ({ ...prev, [l]: { url, rows: [], naturalW: 0, naturalH: 0 } }))
            } finally {
                loadingRef.current.delete(l)
            }
        }
        Promise.all([layerN, layerN + 1].filter(has).map(load)).then(() => {
            if (epochRef.current !== epoch) return
            // SKY-193: the layers the next ＞ / ＜ will show, decoded while the user looks at
            // this pair, so the switch itself only has to recolour (download + decode were
            // the visible part of the wait on a slow network / tablet).
            ;[layerN + 2, layerN - 1].filter(has).forEach(load)
        })
        // Intentionally keyed on attachments + active layer only (images cache read via ref).
    }, [attachments, layerN])

    // 3) SKY-32: repaint the visible layers by mesh status. Re-runs when the status
    //    (statusMap) or the orange selection changes, so colours stay live.
    //    SKY-193: a layer is repainted only when ITS inputs changed — its pixels, its own cells'
    //    statuses, its own selection — and the result is cached per layer. Before, every
    //    status/selection/layer change repainted both visible layers, and a ＞ repainted the
    //    layer that stays on screen twice (on the layer change, again when the new PNG arrived);
    //    on a tablet that repaint, not the download, was most of the wait. ＜ back to a layer
    //    already painted now costs nothing.
    useEffect(() => {
        const want = [layerN, layerN + 1]
        const next: Record<number, string> = {}
        // built from the stable selSig (not the selectedCells prop) — see SKY-131 above
        const selected = new Set(selSig ? selSig.split('|') : [])
        const statusParts = statusSig ? statusSig.split('|') : []
        want.forEach((l) => {
            const img = images[l]
            if (!img?.imageData) return
            // this layer's share of statusSig / selSig (entries start with `${layer}-`) = cache key
            const sig = statusParts.filter(s => s.startsWith(`${l}-`)).join('|')
                + '#' + Array.from(selected).filter(s => s.startsWith(`${l}-`)).sort().join('|')
            const hit = recolorCache.current[l]
            if (hit && hit.src === img.imageData && hit.sig === sig) { next[l] = hit.url; return }
            const validRows = img.rows
            const snapRow = (blue: number): number => {
                if (!validRows.length) return blue
                let best = validRows[0], bestD = Math.abs(blue - best)
                for (const r of validRows) { const d = Math.abs(blue - r); if (d < bestD) { bestD = d; best = r } }
                return best
            }
            const colorForRow = (blue: number): [number, number, number] => {
                const row = snapRow(blue)
                if (selected.has(`${l}-${row}`)) return COLOR_RGB.selected
                return cellColorRgb(statusMap[`${l}-${row}`])
            }
            const url = recolorMeshToDataUrl(img.imageData, colorForRow)
            recolorCache.current[l] = { src: img.imageData, sig, url }
            next[l] = url
        })
        if (Object.keys(next).length) {
            // same URLs as before (all cache hits) -> keep the state object, no re-render
            setRecolored(prev => (Object.keys(next).some(k => prev[+k] !== next[+k]) ? { ...prev, ...next } : prev))
        }
        // SKY-131: depend on STABLE signatures (not the statusMap/selectedCells object refs) so
        // this expensive canvas recolour runs only when statuses/selection actually change.
    }, [images, statusSig, selSig, layerN])

    // SKY-32: map a click on the rendered (recoloured) image back to a source pixel,
    // read its Blue channel (= mesh row) and fire onCellClick — pixel-accurate, so the
    // click always matches the cell the user actually sees.
    const handleImgClick = (layer: number) => (e: React.MouseEvent<HTMLImageElement>) => {
        const img = images[layer]
        if (!onCellClick || !img?.imageData) return
        const target = e.currentTarget
        const rect = target.getBoundingClientRect()
        if (!rect.width || !rect.height) return
        const { width, height, data } = img.imageData
        const sx = Math.min(width - 1, Math.max(0, Math.floor((e.clientX - rect.left) / rect.width * width)))
        const sy = Math.min(height - 1, Math.max(0, Math.floor((e.clientY - rect.top) / rect.height * height)))
        const idx = (sy * width + sx) * 4
        if (!isMarkerPixel(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])) return
        const validRows = img.rows
        let row = data[idx + 2]
        if (validRows.length) {
            let bestD = Infinity
            for (const r of validRows) { const d = Math.abs(data[idx + 2] - r); if (d < bestD) { bestD = d; row = r } }
        }
        const st = statusMap[`${layer}-${row}`]
        onCellClick({ layer, row, meshId: st?.id })
    }

    const renderPanel = (layer: number, title: string) => {
        // #8506: the image URL is known from the attachment as soon as GetAttachments
        // returns — render it independently of the PNG scan so a slow/failed scan can
        // never leave the panel with a broken/blank image (which is what happened on 現場).
        const hasAtt = attachments.some(a => a.layer === layer)
        // SKY-32: always render the RECOLOURED mesh (data URL). Never fall back to the raw red
        // PNG — its transient load error used to fire onError and hide the panel for good.
        const url = recolored[layer]
        return (
            <div className="overlay2d-panel" style={panelStyle}>
                {/* SKY-124: mesh sized RELATIVE to its container (not a fixed 360px cap) so it
                    (a) scales up on 拡大 as the panel grows — the old min(100%,360px) stayed 360px
                    at every zoom, and (b) keeps a consistent proportion with gray margins around it
                    regardless of the source PNG's aspect ratio, matching the 3D model presentation. */}
                <div style={{ ...meshBoxStyle, padding: fullScreen ? MESH_PAD_FULL : 0 }}>
                    {url ? (
                        <img
                            src={url}
                            alt={title}
                            onClick={handleImgClick(layer)}
                            style={fullScreen ? {
                                // SKY-124 zoomed (拡大後): fill the flex-sized box and let
                                // object-fit scale the mesh up, using the extra panel height freed on zoom.
                                width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                                cursor: onCellClick ? 'pointer' : 'default',
                            } : {
                                // SKY-124 not-zoomed (拡大前): keep the ORIGINAL display (customer
                                // request) — compact, centred, capped at 360px as before.
                                maxWidth: '100%', maxHeight: 'min(100%, 360px)',
                                width: 'auto', height: 'auto', display: 'block',
                                cursor: onCellClick ? 'pointer' : 'default',
                            }}
                        />
                    ) : (
                        <div style={emptyStyle}>{(loading || hasAtt) ? '読み込み中…' : 'png画像がありません。'}</div>
                    )}
                </div>
                {/* ⑳㉑ / ⑫⑬: current-layer label 「n／m層目」 under each image */}
                <div style={layerLabelStyle}>{title}</div>
            </div>
        )
    }

    if (!locationId) {
        return <div style={{ ...containerStyle, background: '#fff' }} />
    }

    const baseIndex = baseLayers.indexOf(layerN)
    const totalLayers = layers.length
    // ㉒㉓㉔㉕ / ⑭⑮⑯⑰: pair-base navigation 先頭・前・次・末尾
    const goFirst = () => baseLayers.length && setLayer(baseLayers[0])
    const goPrev = () => baseIndex > 0 && setLayer(baseLayers[baseIndex - 1])
    const goNext = () => baseIndex > -1 && baseIndex < baseLayers.length - 1 && setLayer(baseLayers[baseIndex + 1])
    const goLast = () => baseLayers.length && setLayer(baseLayers[baseLayers.length - 1])
    const atFirst = baseIndex <= 0
    const atLast = baseIndex === -1 || baseIndex >= baseLayers.length - 1

    return (
        <div style={containerStyle}>
            {/* two images: left = N (先行), right = N+1 (後続) — order per the 画面設計書 mockups.
                SKY-34: flex:1 + stretch so the pair fills the vertical space of the 打重ね area. */}
            <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'stretch', flex: 1, minHeight: 0 }}>
                {renderPanel(layerN, `${layerN}／${totalLayers}層目`)}
                {renderPanel(layerN + 1, `${layerN + 1}／${totalLayers}層目`)}
            </div>
            <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 8 }}>
                <button style={navBtnStyle} disabled={atFirst} onClick={goFirst}>＜＜</button>
                <button style={navBtnStyle} disabled={atFirst} onClick={goPrev}>＜</button>
                {/* SKY-142: spec leaves a wider gap between the back pair and the forward pair */}
                <button style={{ ...navBtnStyle, marginLeft: 29 }} disabled={atLast} onClick={goNext}>＞</button>
                <button style={navBtnStyle} disabled={atLast} onClick={goLast}>＞＞</button>
            </div>
        </div>
    )
}

const containerStyle: React.CSSProperties = {
    // SKY-34: flex:1 fills the col-6 (now a flex column) so the grid can be enlarged into the
    // empty vertical area the customer boxed in red, instead of leaving a gray gap below.
    display: 'flex', flexDirection: 'column', width: '100%', flex: 1, minHeight: 320,
    // SKY-142: spec shows each layer as its OWN framed box on a WHITE page background,
    //   not one continuous gray block. The gray now lives on the mesh box only.
    background: '#fff', padding: 8,
}
const layerLabelStyle: React.CSSProperties = {
    // SKY-142: spec renders 「n／m層目」 as plain centred text under each frame — no white
    //   full-width bar and no border. Larger type to match the mockup.
    textAlign: 'center', fontSize: 17, marginTop: 6, padding: '2px 12px',
}
const navBtnStyle: React.CSSProperties = {
    // SKY-142: sizes measured from the spec mockup — 60x38 face, light gray (#EFEFEF).
    minWidth: 60, height: 38, fontSize: 15, background: '#EFEFEF', border: '1px solid #999',
}
const panelStyle: React.CSSProperties = {
    // SKY-124: fill the column but centre the (capped) mesh so it sits proportionally with
    // gray margins around it — mirrors how the 3D model view presents its model.
    flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
    minHeight: 0, background: '#fff',
}
// SKY-124: not-zoomed (拡大前) keeps the original capped display (360px). Only zoomed (拡大後)
//   uses object-fit + a small padding (MESH_PAD_FULL) so the mesh scales up on zoom
//   (fixes the reported "size unchanged between 拡大前/拡大後").
const MESH_PAD_FULL = '2%'
const meshBoxStyle: React.CSSProperties = {
    // SKY-142: the framed gray box per layer — colours sampled from the spec mockup
    //   (background #808080, olive #808000 border).
    flex: 1, minHeight: 0, boxSizing: 'border-box',
    background: '#808080', border: '2px solid #808000',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const emptyStyle: React.CSSProperties = {
    background: '#fff', color: '#333', width: '90%', height: 260,
    display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ccc',
}

export default Overlay2D
